import React, { useState, useRef } from 'react';
import { X, FileText, Loader2, Wand2, Trash2, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';
import { isSessionExpired } from '../../utils/adminSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_IMAGES = 5;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

/** Modal "Brouillon IA" : le coach colle un texte libre (liste d'exercices, séries/reps/repos,
 * supersets...) et/ou importe des photos de notes manuscrites ou de captures d'écran — l'IA
 * (vision) reconstruit le programme structuré fidèlement, prêt à être affiné dans le formulaire
 * d'édition habituel (mêmes exercices s'ils existent déjà dans la bibliothèque, sinon créés
 * automatiquement à l'enregistrement, comme le reste de l'admin). */
export const AIDraftParserModal = ({ csrfToken, onClose, onGenerated }) => {
  const [text, setText] = useState('');
  const [images, setImages] = useState([]); // [{ id, name, dataUrl }]
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const fetchWithCsrf = (url, options = {}) => fetch(url, {
    ...options, credentials: 'include',
    headers: { ...options.headers, 'X-CSRF-Token': csrfToken },
  });

  const handleFiles = (files) => {
    const list = Array.from(files || []).filter((f) => ACCEPTED_TYPES.includes(f.type));
    if (list.length === 0) { toast.error('Formats acceptés : JPEG, PNG, WEBP.'); return; }
    if (images.length + list.length > MAX_IMAGES) { toast.error(`Maximum ${MAX_IMAGES} images.`); return; }
    list.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => setImages((prev) => [...prev, { id: `${Date.now()}-${Math.random()}`, name: file.name, dataUrl: reader.result }]);
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (id) => setImages((prev) => prev.filter((im) => im.id !== id));

  const poll = (jobId, attempt = 0) => {
    if (attempt >= 60) { setBusy(false); toast.error("L'analyse prend trop de temps. Réessayez plus tard."); return; }
    fetch(`${BACKEND_URL}/api/admin/programs-pro/ai-generate/${jobId}`, { credentials: 'include' })
      .then((r) => {
        if (isSessionExpired(r)) { setBusy(false); return Promise.reject(null); }
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then((job) => {
        if (job.status === 'generating') { setTimeout(() => poll(jobId, attempt + 1), 3000); return; }
        setBusy(false);
        if (job.status === 'ready') onGenerated(job.program);
        else toast.error(job.error || "L'analyse du brouillon a échoué. Réessayez.");
      })
      .catch((r) => { if (r) { setBusy(false); toast.error('Connexion perdue pendant l\u2019analyse. Réessayez.'); } });
  };

  const submit = () => {
    if (!text.trim() && images.length === 0) { toast.error('Écrivez votre brouillon ou ajoutez au moins une image.'); return; }
    setBusy(true);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/programs-pro/ai-draft-parse`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: text.trim() || null, images: images.map((im) => im.dataUrl) }),
    })
      .then((r) => {
        if (isSessionExpired(r)) { setBusy(false); return Promise.reject(null); }
        return r.ok ? r.json() : r.json().then((j) => Promise.reject(j));
      })
      .then((d) => poll(d.job_id))
      .catch((j) => { if (j) toast.error(j?.detail || "L'analyse du brouillon a échoué. Réessayez."); setBusy(false); });
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="ai-draft-parser-modal">
      <div className="bg-zinc-900 border border-emerald-500/30 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-400" /> Brouillon IA
          </h3>
          <button onClick={onClose} disabled={busy} className="text-zinc-500 hover:text-white transition-colors" data-testid="ai-draft-parser-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {busy ? (
          <div className="flex flex-col items-center justify-center py-14 gap-3" data-testid="ai-draft-parser-generating">
            <Loader2 className="w-10 h-10 text-emerald-400 animate-spin" />
            <p className="text-zinc-300 text-sm font-semibold">L&apos;IA analyse votre brouillon...</p>
            <p className="text-zinc-500 text-xs">Lecture du texte{images.length > 0 ? ' et des images' : ''}, jusqu&apos;à 60 secondes.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-zinc-500">Collez vos notes (séances, exercices, séries, reps, repos, supersets...) et/ou importez une photo/capture — l&apos;IA reconstruit le programme exact, prêt à ajuster.</p>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Votre brouillon (texte libre)</label>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={7}
                placeholder={"Ex: 3 séances, 1 push 1 pull 1 leg. Push: développé couché barre, écarté poulie vis-à-vis, extensions triceps poulie haute. 4 séries de 8-12, 60s de repos, 2 exercices en superset. Séance de 60 min."}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm resize-none focus:border-emerald-500/50 focus:outline-none"
                data-testid="ai-draft-text-input" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Images (photos de notes, captures d&apos;écran — optionnel, max {MAX_IMAGES})</label>
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" multiple
                onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
                className="hidden" data-testid="ai-draft-image-input" />
              <button onClick={() => fileInputRef.current?.click()} disabled={images.length >= MAX_IMAGES}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 border border-dashed border-white/20 rounded-lg text-zinc-400 text-sm hover:border-emerald-500/40 hover:text-emerald-300 transition-colors disabled:opacity-50"
                data-testid="ai-draft-image-upload-btn">
                <UploadCloud className="w-4 h-4" /> Importer des images
              </button>
              {images.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-2" data-testid="ai-draft-image-previews">
                  {images.map((im) => (
                    <div key={im.id} className="relative aspect-square rounded-lg overflow-hidden bg-zinc-800 border border-white/10" data-testid={`ai-draft-image-preview-${im.id}`}>
                      <img src={im.dataUrl} alt={im.name} className="w-full h-full object-cover" />
                      <button onClick={() => removeImage(im.id)} className="absolute top-1 right-1 p-1 bg-black/60 rounded-full text-white hover:bg-red-500/80 transition-colors" data-testid={`ai-draft-image-remove-${im.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button onClick={submit} className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-500 text-white font-bold rounded-xl hover:scale-105 transition-transform" data-testid="ai-draft-parse-submit-btn">
                <Wand2 className="w-4 h-4" /> Analyser et créer le brouillon
              </button>
              <button onClick={onClose} className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 text-zinc-300 rounded-xl hover:border-white/30 transition-colors" data-testid="ai-draft-parse-cancel-btn">
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIDraftParserModal;
